/**
 * MiniSankeyFlow Component
 * WP-DASH-2: Main container for dashboard mini Sankey visualization
 *
 * Replaces MiniSankeyPreview with an actual three-column Sankey flow diagram.
 * Shows money flow from income sources through accounts to expense categories.
 */

import { Link } from 'react-router-dom';
import Card, { CardHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import { useFlowData } from '@/components/flow/hooks/useFlowData';
import type { FlowSnapshot } from '@/services/flows';
import { MiniFlowCanvas } from './MiniFlowCanvas';
import { FlowSummaryBar } from './FlowSummaryBar';

// ============================================================================
// Types
// ============================================================================

interface MiniSankeyFlowProps {
  flowSnapshot: FlowSnapshot | null;
  isLoading?: boolean;
  hasConnections: boolean;
  /** WP-CORE-9: shown when the flow was refused by the household's calculation limit (429) */
  rateLimitMessage?: string | null;
}

// ============================================================================
// Skeleton Component
// ============================================================================

function MiniSankeySkeleton() {
  return (
    <div className="space-y-4 py-4">
      {/* Column headers skeleton */}
      <div className="flex justify-between px-4">
        <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
        <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
        <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
      </div>

      {/* Flow diagram skeleton */}
      <div className="flex items-center justify-between gap-4 px-4 py-8">
        {/* Left column - income */}
        <div className="flex flex-col gap-2">
          <div className="h-10 w-20 animate-pulse rounded bg-green-100" />
          <div className="h-8 w-20 animate-pulse rounded bg-green-100" />
        </div>

        {/* Center column - accounts */}
        <div className="flex flex-col gap-2">
          <div className="h-12 w-20 animate-pulse rounded bg-blue-100" />
          <div className="h-10 w-20 animate-pulse rounded bg-blue-100" />
          <div className="h-8 w-20 animate-pulse rounded bg-blue-100" />
        </div>

        {/* Right column - expenses */}
        <div className="flex flex-col gap-2">
          <div className="h-10 w-20 animate-pulse rounded bg-red-100" />
          <div className="h-8 w-20 animate-pulse rounded bg-red-100" />
          <div className="h-6 w-20 animate-pulse rounded bg-red-100" />
        </div>
      </div>

      {/* Summary bar skeleton */}
      <div className="mx-4 h-12 animate-pulse rounded-lg bg-gray-100" />
    </div>
  );
}

// ============================================================================
// Empty State Components
// ============================================================================

interface EmptyStateProps {
  variant: 'no-connections' | 'no-data';
}

function EmptyState({ variant }: EmptyStateProps) {
  if (variant === 'no-connections') {
    return (
      <div className="py-8 text-center">
        <svg
          className="mx-auto h-16 w-16 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6"
          />
        </svg>
        <h3 className="mt-4 text-sm font-medium text-gray-900">
          Connect a bank to see your money flow
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          Your Sankey flow diagram will visualize where your money goes.
        </p>
        <div className="mt-4">
          <Link to="/connections">
            <Button variant="primary" size="sm">
              Connect Bank
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 text-center">
      <svg
        className="mx-auto h-16 w-16 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
      <h3 className="mt-4 text-sm font-medium text-gray-900">No flow data yet</h3>
      <p className="mt-2 text-sm text-gray-500">
        Once your transactions sync, your money flow diagram will appear here.
      </p>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * MiniSankeyFlow - Dashboard mini Sankey visualization
 *
 * Shows a three-column Sankey diagram:
 * - Inflows: Income sources (salary, dividends, etc.)
 * - Accounts: Bank accounts with balances
 * - Outflows: Expense categories (rent, food, etc.)
 *
 * Edges show money flow with thickness proportional to amount.
 */
export default function MiniSankeyFlow({
  flowSnapshot,
  isLoading,
  hasConnections,
  rateLimitMessage = null,
}: MiniSankeyFlowProps) {
  // Transform flow snapshot to visualization format
  const flowData = useFlowData(flowSnapshot ?? undefined);

  // For mini Sankey, only show Tier A accounts (checking/credit cards)
  // This gives a simplified summary view without expanded tiers
  const tierAAccountNodes = flowData?.accountNodes.filter(
    (node) => node.tier === 'A'
  ) ?? [];

  // Determine if we have displayable data
  const hasData = flowData !== null && (
    flowData.inflowNodes.length > 0 ||
    flowData.outflowNodes.length > 0 ||
    tierAAccountNodes.length > 0
  );

  // Show CTA button only when we have data
  const showCta = hasData && flowData.summary.totalIncome > 0;

  return (
    <Card>
      <CardHeader
        title="Money Flow"
        description="Where your money goes (last 30 days)"
        action={
          showCta ? (
            <Link to="/flow">
              <Button variant="outline" size="sm">
                Explore Full Flow
              </Button>
            </Link>
          ) : null
        }
      />

      {isLoading ? (
        <MiniSankeySkeleton />
      ) : !hasConnections ? (
        <EmptyState variant="no-connections" />
      ) : rateLimitMessage && !hasData ? (
        <div className="p-4">
          <Alert variant="warning" title="Flow limit reached">
            {rateLimitMessage}
          </Alert>
        </div>
      ) : !hasData || !flowData ? (
        <EmptyState variant="no-data" />
      ) : (
        <>
          {/* Mini Sankey Canvas - shows only Tier A accounts for summary */}
          <MiniFlowCanvas
            inflowNodes={flowData.inflowNodes}
            accountNodes={tierAAccountNodes}
            outflowNodes={flowData.outflowNodes}
            edges={flowData.edges}
          />

          {/* Summary bar */}
          <FlowSummaryBar
            totalIncome={flowData.summary.totalIncome}
            totalExpenses={flowData.summary.totalExpenses}
            netFlow={flowData.summary.netFlow}
            savingsRate={flowData.summary.savingsRate}
          />

          {/* Link to full flow page */}
          <div className="mt-4 text-center">
            <Link
              to="/flow"
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              View interactive Sankey diagram
            </Link>
          </div>
        </>
      )}
    </Card>
  );
}
